<template>
    <div class="form-container">
        <h2 style="margin: 0 0 16px 0; color: var(--color-ds-primary);">Run Workflow</h2>
        <div v-if="actions.length === 0" class="status-warning">
            No workflow actions found. Please add actions using the Import Data or Export Data buttons first.
        </div>

        <div v-else>
            <div class="form-section">
                <h3>Workflow Actions ({{selectedActions.length}} of {{actions.length}} selected)</h3>

                <div style="display: flex; gap: 8px; margin-bottom: 12px;">
                    <button class="button-small" @click="refreshActions" :disabled="running">Refresh</button>
                    <button class="button-small" @click="selectAll" :disabled="running">Select All</button>
                    <button class="button-small" @click="selectNone" :disabled="running">Select None</button>
                </div>

                <div style="margin: 12px 0;">
                    <div v-for="(action, index) in actions" :key="action.name" style="margin: 8px 0; display: flex; align-items: flex-start;">
                        <input
                            type="checkbox"
                            :id="'action-' + index"
                            v-model="action.selected"
                            :disabled="running"
                            style="margin-right: 8px; margin-top: 3px; cursor: pointer;"
                        />
                        <label :for="'action-' + index" style="cursor: pointer; flex: 1;">
                            <strong>{{ action.name }}</strong> ({{ action.type }})
                        </label>
                    </div>
                </div>

                <button class="button button-full" @click="runWorkflow" :disabled="running || selectedActions.length === 0">
                    {{ running ? 'Running...' : `Run ${selectedActions.length} Action${selectedActions.length !== 1 ? 's' : ''}` }}
                </button>
            </div>

            <div v-if="statusMessage" :class="isError ? 'status-error' : 'status-success'">
                {{ statusMessage }}
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { showMessage } from '../utils/messages';
import { executeWorkflow } from '../services/workflowExecutionService';

const actions = ref([]);
const running = ref(false);
const statusMessage = ref('');
const isError = ref(false);

// Computed property for selected actions
const selectedActions = computed(() => {
    return actions.value.filter(action => action.selected);
});

// Select all actions
const selectAll = () => {
    actions.value.forEach(action => {
        action.selected = true;
    });
};

// Deselect all actions
const selectNone = () => {
    actions.value.forEach(action => {
        action.selected = false;
    });
};

// Refresh actions from the Workflow sheet
const refreshActions = async () => {
    showMessage('Refreshing workflow actions...');
    await loadWorkflowActions();
    showMessage('Workflow actions refreshed');
};

// Read workflow actions from the Workflow sheet
const loadWorkflowActions = async (preserveSelection = false) => {
    try {
        // Save current selection state if preserving
        const previousSelections = {};
        if (preserveSelection) {
            actions.value.forEach(action => {
                previousSelections[action.name] = action.selected;
            });
        }

        await window.Excel.run(async (context) => {
            const workbook = context.workbook;
            const workflowSheet = workbook.worksheets.getItemOrNullObject('Workflow');
            workflowSheet.load('name');
            await context.sync();

            if (workflowSheet.isNullObject) {
                showMessage('Workflow sheet not found', true);
                return;
            }

            const usedRange = workflowSheet.getUsedRange();
            usedRange.load('values, columnCount');
            await context.sync();

            const values = usedRange.values;
            const columnCount = usedRange.columnCount;

            // Parse workflow items (each action is in a column pair: keys and values)
            const parsedActions = [];

            for (let col = 0; col < columnCount; col += 2) {
                const keys = [];
                const vals = [];

                // Read keys column
                for (let row = 0; row < values.length; row++) {
                    const key = values[row][col];
                    if (key) {
                        keys.push(key);
                    } else {
                        break; // Stop when we hit an empty cell
                    }
                }

                // Read values column
                if (col + 1 < columnCount) {
                    for (let row = 0; row < keys.length; row++) {
                        vals.push(values[row][col + 1]);
                    }
                }

                // Create action object
                if (keys.length > 0 && vals.length > 0) {
                    const params = {};
                    for (let i = 0; i < keys.length; i++) {
                        params[keys[i]] = vals[i];
                    }

                    if (params.ActionName && params.ActionType) {
                        // Preserve previous selection state if available, otherwise default to true
                        const isSelected = preserveSelection && Object.prototype.hasOwnProperty.call(previousSelections, params.ActionName)
                            ? previousSelections[params.ActionName]
                            : true;

                        parsedActions.push({
                            name: params.ActionName,
                            type: params.ActionType,
                            params: params,
                            selected: isSelected
                        });
                    }
                }
            }

            actions.value = parsedActions;
        });
    } catch (error) {
        console.error('Error loading workflow actions:', error);
        showMessage('Error loading workflow actions: ' + error.message, true);
    }
};

// Execute the workflow
const runWorkflow = async () => {
    running.value = true;
    statusMessage.value = '';
    isError.value = false;

    try {
        // Reload workflow actions from sheet to get latest values, preserving user's checkbox selections
        showMessage('Reading latest workflow values from sheet...');
        await loadWorkflowActions(true);

        const selected = selectedActions.value;

        if (selected.length === 0) {
            showMessage('Please select at least one action to run', true);
            running.value = false;
            return;
        }

        showMessage(`Starting workflow execution with ${selected.length} action(s)...`);

        // Update status with progress messages
        const updateStatus = (message) => {
            statusMessage.value = message;
            showMessage(message);
        };

        // Execute only the selected actions
        const result = await executeWorkflow(selected, updateStatus);

        statusMessage.value = `✅ ${result.message}`;
        isError.value = false;
        showMessage(statusMessage.value);

    } catch (error) {
        console.error('Error running workflow:', error);
        statusMessage.value = `❌ Error: ${error.message}`;
        isError.value = true;
        showMessage(statusMessage.value, true);
    } finally {
        running.value = false;
    }
};

onMounted(async () => {
    await loadWorkflowActions();
});
</script>

<style scoped>
label {
    user-select: none;
}
</style>
